import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { 
  ArrowLeft, 
  Edit, 
  CreditCard, 
  Users, 
  Package, 
  CreditCard as CreditCardIcon, 
  BarChart, 
  Download, 
  ChevronUp, 
  ChevronDown,
  Mail,
  Phone,
  MapPin,
  Building,
  FileText,
  ExternalLink,
  MoreHorizontal
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { supabase } from '@/lib/supabase';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { formatDate, formatCurrency, cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import CustomerContactsTable from '@/components/customers/CustomerContactsTable';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import ImportHubspotContactDialog from '@/components/customers/ImportHubspotContactDialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { StatusBadge } from '@/components/common/StatusBadge';

interface SubscriptionItem {
  subscription_plan: {
    name: string;
    billing_period_type?: string;
    prices?: {
      currency: string;
      price_amount: number;
    }[];
  };
  quantity: number;
  unit_price: number;
}

interface SubscriptionResponse {
  id: string;
  state: string;
  start_date: string;
  end_date: string | null;
  next_billing_at: string | null;
  items: SubscriptionItem[];
}

interface Subscription {
  id: string;
  state: string;
  start_date: string;
  end_date: string | null;
  next_billing_at: string | null;
  subscription_plan: {
    name: string;
    prices?: {
      currency: string;
      price_amount: number;
    }[];
    billing_period_type?: string;
  };
  quantity: number;
  unit_price: number;
}

interface Customer {
  id: string;
  name: string;
  business_identification_number?: string;
  legal_name?: string;
  address?: string;
  address2?: string;
  city?: string;
  state?: string;
  zip?: string;
  phone?: string;
  domain?: string;
  logo_url?: string;
  created_at?: string;
  updated_at?: string;
  description?: string;
  status?: string;
  email?: string;
  hubspot_owner_id?: string;
  responsible_user_name?: string;
  industry?: string;
}

const CustomerDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [subscriptionsLoading, setSubscriptionsLoading] = useState(true);
  const [contactsCount, setContactsCount] = useState<number>(0);
  const [contactsLoading, setContactsLoading] = useState(true);
  const [failedImage, setFailedImage] = useState(false);
  const [isImportContactDialogOpen, setIsImportContactDialogOpen] = useState(false);
  const [contactsRefreshTrigger, setContactsRefreshTrigger] = useState(0);
  const [activeTab, setActiveTab] = useState('overview');

  // Função para obter as iniciais do nome do cliente
  const getInitials = (name: string) => {
    if (!name) return 'C';
    return name.charAt(0).toUpperCase();
  };

  // Função para lidar com erro de carregamento da imagem
  const handleImageError = () => {
    setFailedImage(true);
  };

  useEffect(() => {
    if (id) {
      fetchCustomer(id);
      fetchCustomerSubscriptions(id);
      fetchContactsCount(id);
    }
  }, [id]);

  const fetchCustomer = async (customerId: string) => {
    try {
      const { data, error } = await supabase
        .from('customers')
        .select('*')
        .eq('id', customerId)
        .single();

      if (error) throw error;
      setCustomer(data as Customer);
    } catch (error) {
      console.error('Erro ao buscar cliente:', error);
      toast.error('Não foi possível carregar os dados do cliente.');
    } finally {
      setLoading(false);
    }
  };

  const fetchContactsCount = async (customerId: string) => {
    try {
      setContactsLoading(true);
      
      // Directly count contacts for the customer
      const { count, error: contactsError } = await supabase
        .from('customer_contacts')
        .select('*', { count: 'exact', head: true })
        .eq('customer_id', customerId)
        .eq('is_active', true);

      if (contactsError) {
        console.error('Erro ao contar contatos:', contactsError);
        throw contactsError;
      }
      
      setContactsCount(count || 0);
    } catch (error) {
      console.error('Erro ao buscar contagem de contatos:', error);
      toast.error('Não foi possível carregar a contagem de contatos.');
    } finally {
      setContactsLoading(false);
    }
  };

  const fetchCustomerSubscriptions = async (customerId: string) => {
    try {
      setSubscriptionsLoading(true);
      
      // Primeiro, buscar os relacionamentos do cliente
      const { data: relationshipsData, error: relationshipsError } = await supabase
        .from('customer_relationship')
        .select('id')
        .eq('customer_id', customerId);
        
      if (relationshipsError) {
        console.error('Erro ao buscar relacionamentos:', relationshipsError);
        throw relationshipsError;
      }
      
      if (!relationshipsData || relationshipsData.length === 0) {
        setSubscriptions([]);
        setSubscriptionsLoading(false);
        return;
      }
      
      // Extrair os IDs dos relacionamentos
      const relationshipIds = relationshipsData.map(rel => rel.id);
      
      // Buscar as assinaturas para esses relacionamentos
      const { data: subscriptionsData, error: subscriptionsError } = await supabase
        .from('subscriptions')
        .select('id, state, start_date, end_date, next_billing_at')
        .in('customer_relationship_id', relationshipIds)
        .order('start_date', { ascending: false });

      if (subscriptionsError) {
        console.error('Erro ao buscar assinaturas:', subscriptionsError);
        throw subscriptionsError;
      }
      
      if (!subscriptionsData || subscriptionsData.length === 0) {
        setSubscriptions([]);
        setSubscriptionsLoading(false);
        return;
      }
      
      // Array para armazenar as assinaturas processadas
      const processedSubscriptions: Subscription[] = [];
      
      // Para cada assinatura, buscar os itens da assinatura
      for (const subscription of subscriptionsData) {
        try {
          const { data: itemData, error: itemError } = await supabase
            .from('subscription_items')
            .select(`
              id,
              quantity, 
              unit_price,
              subscription_plan_id
            `)
            .eq('subscription_id', subscription.id)
            .limit(1)
            .maybeSingle();

          if (itemError && itemError.code !== 'PGRST116') {
            console.error('Erro ao buscar item da assinatura:', itemError);
            // Adicionar assinatura com valores padrão
            processedSubscriptions.push({
              ...subscription,
              subscription_plan: { 
                name: 'Plano não encontrado', 
                prices: [{ currency: 'BRL', price_amount: 0 }],
                billing_period_type: 'monthly'
              },
              quantity: 1,
              unit_price: 0
            });
            continue;
          }
          
          if (!itemData) {
            // Adicionar assinatura com valores padrão quando não há items
            processedSubscriptions.push({
              ...subscription,
              subscription_plan: { 
                name: 'Plano não encontrado', 
                prices: [{ currency: 'BRL', price_amount: 0 }],
                billing_period_type: 'monthly' 
              },
              quantity: 1,
              unit_price: 0
            });
            continue;
          }
          
          // Buscar os detalhes do plano
          const { data: planData, error: planError } = await supabase
            .from('subscription_plans')
            .select(`
              name, 
              billing_period_type,
              prices:subscription_plan_prices (
                currency,
                price_amount
              )
            `)
            .eq('id', itemData.subscription_plan_id)
            .single();
            
          if (planError) {
            console.error('Erro ao buscar plano:', planError);
            // Adicionar assinatura com valores padrão para o plano
            processedSubscriptions.push({
              ...subscription,
              subscription_plan: { 
                name: 'Plano não encontrado', 
                prices: [{ currency: 'BRL', price_amount: 0 }],
                billing_period_type: 'monthly'
              },
              quantity: itemData.quantity || 1,
              unit_price: itemData.unit_price || 0
            });
            continue;
          }
          
          // Garantir que prices sempre seja um array válido
          const validPrices = Array.isArray(planData?.prices) && planData.prices.length > 0 
            ? planData.prices 
            : [{ currency: 'BRL', price_amount: 0 }];
          
          // Adicionar assinatura com todos os dados
          processedSubscriptions.push({
            ...subscription,
            subscription_plan: {
              name: planData?.name || 'Sem nome',
              prices: validPrices,
              billing_period_type: planData?.billing_period_type || 'monthly'
            },
            quantity: itemData.quantity || 1,
            unit_price: itemData.unit_price || 0
          });
        } catch (error) {
          console.error('Erro ao processar assinatura:', error);
          // Adicionar assinatura com valores padrão
          processedSubscriptions.push({
            ...subscription,
            subscription_plan: { 
              name: 'Erro ao carregar plano', 
              prices: [{ currency: 'BRL', price_amount: 0 }],
              billing_period_type: 'monthly'
            },
            quantity: 1,
            unit_price: 0
          });
        }
      }
      
      setSubscriptions(processedSubscriptions);
    } catch (error) {
      console.error('Erro ao buscar assinaturas:', error);
      toast.error('Não foi possível carregar as assinaturas do cliente.');
      // Em caso de erro, definir um array vazio
      setSubscriptions([]);
    } finally {
      setSubscriptionsLoading(false);
    }
  };

  // Format CNPJ
  const formatDocument = (doc: string | null) => {
    if (!doc) return '-';
    
    // Remove non-numeric characters
    const numbers = doc.replace(/\D/g, '');
    
    // Format as CNPJ (xx.xxx.xxx/xxxx-xx)
    if (numbers.length === 14) {
      return numbers.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
    }
    // Format as CPF (xxx.xxx.xxx-xx)
    else if (numbers.length === 11) {
      return numbers.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    }
    
    return doc;
  };

  // Calcular métricas
  const getActiveSubscriptionsCount = () => {
    return subscriptions.filter(sub => sub.state === 'active').length;
  };

  const getActivePlansCount = () => {
    // Conta planos únicos ativos
    const uniquePlans = new Set();
    subscriptions
      .filter(sub => sub.state === 'active')
      .forEach(sub => {
        uniquePlans.add(sub.subscription_plan?.name || 'Plano Desconhecido');
      });
    return uniquePlans.size;
  };

  const getRecurringRevenue = () => {
    return subscriptions
      .filter(sub => sub.state === 'active')
      .reduce((total, sub) => {
        return total + (sub.unit_price * sub.quantity);
      }, 0);
  };

  const getBillingFrequency = (periodType: string) => {
    const periodTypeMap: Record<string, string> = {
      monthly: 'Mensal',
      yearly: 'Anual',
      quarterly: 'Trimestral',
      semiannually: 'Semestral',
      weekly: 'Semanal',
      daily: 'Diário',
      once: 'Único'
    };

    return periodTypeMap[periodType] || periodType;
  };

  // New function to handle contact imported
  const handleContactImported = () => {
    setContactsRefreshTrigger(prev => prev + 1);
    fetchContactsCount(id as string);
  };

  // Função para traduzir o campo industry para PT-BR
  const translateIndustry = (industry?: string): string => {
    if (!industry) return '';
    
    const industryMap: Record<string, string> = {
      'agriculture': 'Agricultura',
      'finance': 'Finanças',
      'technology': 'Tecnologia',
      'healthcare': 'Saúde',
      'education': 'Educação',
      'retail': 'Varejo',
      'manufacturing': 'Manufatura',
      'construction': 'Construção',
      'entertainment': 'Entretenimento',
      'transport': 'Transporte',
      'energy': 'Energia',
      'real_estate': 'Imobiliário',
      'consulting': 'Consultoria',
      'legal': 'Jurídico'
    };
    
    return industryMap[industry.toLowerCase()] || industry;
  };

  // Traduzir o status das assinaturas para PT-BR
  const getSubscriptionStatusLabel = (status: string) => {
    const statusMap: Record<string, string> = {
      active: 'Ativa',
      inactive: 'Inativa',
      pending: 'Pendente',
      suspended: 'Suspensa',
      cancelled: 'Cancelada',
      trial: 'Teste',
      expired: 'Expirada'
    };

    return statusMap[status] || status;
  };

  if (loading) {
    return (
      <div className="w-full p-6">
        <div className="flex flex-col gap-4 w-full max-w-7xl mx-auto">
          <Skeleton className="h-16 w-1/3" />
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Skeleton className="h-32" />
            <Skeleton className="h-32" />
            <Skeleton className="h-32" />
            <Skeleton className="h-32" />
          </div>
          <Skeleton className="h-64 mt-4" />
        </div>
      </div>
    );
  }

  return (
    <div className="w-full p-6">
      <div className="max-w-7xl mx-auto">
        {/* Cabeçalho moderno */}
        <div className="bg-white dark:bg-gray-950 rounded-xl shadow-sm border p-6 mb-6">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div>
                {customer?.domain ? (
                  <Avatar className="h-16 w-16 border-2">
                    <AvatarImage 
                      src={`https://cdn.brandfetch.io/${customer.domain}?c=1idk4AUs2-buvooOUvQ`} 
                      alt={customer.name} 
                      onError={() => setFailedImage(true)}
                    />
                    <AvatarFallback className="text-2xl bg-primary/10 text-primary">
                      {getInitials(customer.name)}
                    </AvatarFallback>
                  </Avatar>
                ) : (
                  <Avatar className="h-16 w-16 border-2">
                    <AvatarFallback className="text-2xl bg-primary/10 text-primary">
                      {getInitials(customer.name)}
                    </AvatarFallback>
                  </Avatar>
                )}
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-2xl font-extrabold">{customer?.name || 'Cliente'}</h1>
                  {customer?.business_identification_number && (
                    <Badge variant="outline" className="ml-2">
                      {formatDocument(customer.business_identification_number)}
                    </Badge>
                  )}
                  <Badge 
                    className="ml-2"
                    variant={customer?.status === 'active' ? 'default' : 'secondary'}
                  >
                    {customer?.status === 'active' ? 'Ativo' : customer?.status || 'N/A'}
                  </Badge>
                </div>
                <div className="flex items-center gap-2 text-muted-foreground mt-1">
                  {customer?.domain && (
                    <a href={`https://${customer.domain}`} target="_blank" rel="noopener noreferrer" 
                       className="flex items-center hover:text-primary text-sm">
                      <ExternalLink className="h-3 w-3 mr-1" />
                      {customer.domain}
                    </a>
                  )}
                  {customer?.industry && (
                    <span className="text-sm flex items-center">
                      <span className="mx-1">•</span>
                      <span>{translateIndustry(customer.industry)}</span>
                    </span>
                  )}
                  {customer?.created_at && (
                    <span className="text-sm flex items-center text-muted-foreground">
                      <span className="mx-1">•</span>
                      <span>Criado: {formatDate(customer.created_at)}</span>
                    </span>
                  )}
                  {customer?.updated_at && (
                    <span className="text-sm flex items-center text-muted-foreground">
                      <span className="mx-1">•</span>
                      <span>Atualizado: {formatDate(customer.updated_at)}</span>
                    </span>
                  )}
                </div>
              </div>
            </div>
            
            <div className="flex flex-wrap gap-2 mt-2 md:mt-0">
              <Button variant="outline" size="sm" className="gap-1" onClick={() => navigate('/customers')}>
                <ArrowLeft className="h-3.5 w-3.5" />
                Voltar
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="gap-1">
                    <MoreHorizontal className="h-3.5 w-3.5" />
                    Mais Ações
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem>Sincronizar com CRM</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
        
        {/* Tabs de navegação */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
          <TabsList className="grid grid-cols-3 md:w-[400px]">
            <TabsTrigger value="overview">Visão Geral</TabsTrigger>
            <TabsTrigger value="subscriptions">Assinaturas</TabsTrigger>
            <TabsTrigger value="contacts">Contatos</TabsTrigger>
          </TabsList>
          
          {/* Conteúdo da Tab: Visão Geral */}
          <TabsContent value="overview" className="mt-4">
            {/* Cards de métricas com tendências */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <Card className="overflow-hidden">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-extrabold text-muted-foreground">Assinaturas Ativas</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <div>
                      {subscriptionsLoading ? (
                        <Skeleton className="h-8 w-16" />
                      ) : (
                        <p className="text-2xl font-extrabold">{getActiveSubscriptionsCount()}</p>
                      )}
                    </div>
                    <div className={cn(
                      "flex items-center px-2 py-1 rounded text-xs font-extrabold",
                      getActiveSubscriptionsCount() > 0 ? "bg-green-50 text-green-600" : "bg-gray-50 text-gray-600"
                    )}>
                      <ChevronUp className="h-3 w-3 mr-1" />
                      <span>Ativo</span>
                    </div>
                  </div>
                  <div className="mt-2 pt-2 border-t">
                    <div className="flex justify-between items-center text-xs text-muted-foreground">
                      <span>Total: {subscriptions.length}</span>
                      <CreditCardIcon className="h-4 w-4" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="overflow-hidden">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-extrabold text-muted-foreground">Planos Ativos</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <div>
                      {subscriptionsLoading ? (
                        <Skeleton className="h-8 w-16" />
                      ) : (
                        <p className="text-2xl font-extrabold">{getActivePlansCount()}</p>
                      )}
                    </div>
                    <div className="flex items-center px-2 py-1 rounded text-xs font-extrabold bg-blue-50 text-blue-600">
                      <Package className="h-3 w-3 mr-1" />
                      <span>Ativo</span>
                    </div>
                  </div>
                  <div className="mt-2 pt-2 border-t">
                    <div className="flex justify-between items-center text-xs text-muted-foreground">
                      <span>Desde: {customer?.created_at ? formatDate(customer.created_at) : 'N/A'}</span>
                      <Package className="h-4 w-4" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="overflow-hidden">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-extrabold text-muted-foreground">Receita Recorrente</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <div>
                      {subscriptionsLoading ? (
                        <Skeleton className="h-8 w-24" />
                      ) : (
                        <p className="text-2xl font-extrabold">{formatCurrency(getRecurringRevenue(), 'BRL')}</p>
                      )}
                    </div>
                    <div className={cn(
                      "flex items-center px-2 py-1 rounded text-xs font-extrabold",
                      getRecurringRevenue() > 0 ? "bg-green-50 text-green-600" : "bg-amber-50 text-amber-600"
                    )}>
                      {getRecurringRevenue() > 0 ? (
                        <>
                          <ChevronUp className="h-3 w-3 mr-1" />
                          <span>Ativo</span>
                        </>
                      ) : (
                        <>
                          <ChevronDown className="h-3 w-3 mr-1" />
                          <span>Inativo</span>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="mt-2 pt-2 border-t">
                    <div className="flex justify-between items-center text-xs text-muted-foreground">
                      <span>Mensal</span>
                      <BarChart className="h-4 w-4" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="overflow-hidden">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-extrabold text-muted-foreground">Contatos</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <div>
                      {contactsLoading ? (
                        <Skeleton className="h-8 w-16" />
                      ) : (
                        <p className="text-2xl font-extrabold">{contactsCount}</p>
                      )}
                    </div>
                    <div className={cn(
                      "flex items-center px-2 py-1 rounded text-xs font-extrabold",
                      contactsCount > 0 ? "bg-violet-50 text-violet-600" : "bg-gray-50 text-gray-600"
                    )}>
                      <Users className="h-3 w-3 mr-1" />
                      <span>{contactsCount > 0 ? 'Ativos' : 'Nenhum'}</span>
                    </div>
                  </div>
                  <div className="mt-2 pt-2 border-t">
                    <div className="flex justify-between items-center text-xs text-muted-foreground">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="text-xs h-7 px-2"
                        onClick={() => setIsImportContactDialogOpen(true)}
                      >
                        <Download className="h-3 w-3 mr-1" />
                        Importar Contato
                      </Button>
                      <Users className="h-4 w-4" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
            
            {/* Informações do cliente + últimas assinaturas */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <Card className="lg:col-span-1">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">Informações do Cliente</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {customer.legal_name && (
                    <div className="flex items-start">
                      <Building className="h-4 w-4 mr-2 mt-0.5 text-muted-foreground" />
                      <div>
                        <p className="text-sm text-muted-foreground">Razão Social</p>
                        <p className="font-extrabold">{customer.legal_name}</p>
                      </div>
                    </div>
                  )}
                  
                  {customer.business_identification_number && (
                    <div className="flex items-start">
                      <FileText className="h-4 w-4 mr-2 mt-0.5 text-muted-foreground" />
                      <div>
                        <p className="text-sm text-muted-foreground">CNPJ</p>
                        <p className="font-extrabold">{formatDocument(customer.business_identification_number)}</p>
                      </div>
                    </div>
                  )}
                  
                  {customer.email && (
                    <div className="flex items-start">
                      <Mail className="h-4 w-4 mr-2 mt-0.5 text-muted-foreground" />
                      <div>
                        <p className="text-sm text-muted-foreground">Email</p>
                        <p className="font-extrabold">{customer.email}</p>
                      </div>
                    </div>
                  )}
                  
                  {customer.phone && (
                    <div className="flex items-start">
                      <Phone className="h-4 w-4 mr-2 mt-0.5 text-muted-foreground" />
                      <div>
                        <p className="text-sm text-muted-foreground">Telefone</p>
                        <p className="font-extrabold">{customer.phone}</p>
                      </div>
                    </div>
                  )}
                  
                  {(customer.address || customer.city || customer.state) && (
                    <div className="flex items-start">
                      <MapPin className="h-4 w-4 mr-2 mt-0.5 text-muted-foreground" />
                      <div>
                        <p className="text-sm text-muted-foreground">Endereço</p>
                        <p className="font-extrabold">
                          {customer.address}
                          {customer.address2 ? `, ${customer.address2}` : ''}
                          {customer.city ? `, ${customer.city}` : ''}
                          {customer.state ? ` - ${customer.state}` : ''}
                          {customer.zip ? ` CEP: ${customer.zip}` : ''}
                        </p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
              
              <Card className="lg:col-span-2">
                <CardHeader className="pb-3 flex flex-row items-center justify-between">
                  <CardTitle className="text-lg">Assinaturas Recentes</CardTitle>
                  <Button variant="ghost" size="sm" className="text-xs h-7 px-2" onClick={() => setActiveTab('subscriptions')}>
                    Ver todas
                  </Button>
                </CardHeader>
                <CardContent>
                  {subscriptionsLoading ? (
                    <div className="space-y-2">
                      <Skeleton className="h-12 w-full" />
                      <Skeleton className="h-12 w-full" />
                    </div>
                  ) : subscriptions.length === 0 ? (
                    <div className="bg-muted/20 rounded-lg p-6 text-center">
                      <p className="text-muted-foreground">Nenhuma assinatura encontrada</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {subscriptions.slice(0, 3).map((subscription) => (
                        <div 
                          key={subscription.id}
                          className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted/30 transition-colors cursor-pointer"
                          onClick={() => navigate(`/subscriptions/${subscription.id}`)}
                        >
                          <div className="flex items-center gap-3">
                            <div className={cn(
                              "w-2 h-10 rounded-full",
                              subscription.state === 'active' ? "bg-green-500" : "bg-gray-300"
                            )}/>
                            <div>
                              <p className="font-extrabold">{subscription.subscription_plan?.name || "Plano não encontrado"}</p>
                              <div className="flex items-center text-xs text-muted-foreground">
                                <span>{getBillingFrequency(subscription.subscription_plan?.billing_period_type || 'monthly')}</span>
                                <span className="mx-1">•</span>
                                <span>Início: {formatDate(subscription.start_date)}</span>
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-extrabold">
                              {formatCurrency(
                                (subscription.unit_price || 0) * (subscription.quantity || 0), 
                                'BRL'
                              )}
                            </p>
                            <Badge variant={subscription.state === 'active' ? 'default' : 'secondary'} className="text-xs">
                              {getSubscriptionStatusLabel(subscription.state)}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
          
          {/* Conteúdo da Tab: Assinaturas */}
          <TabsContent value="subscriptions" className="mt-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Assinaturas</CardTitle>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => navigate('/subscription-wizard/step1')}
                >
                  Nova Assinatura
                </Button>
              </CardHeader>
              <CardContent>
                {subscriptionsLoading ? (
                  <div className="space-y-2">
                    <Skeleton className="h-10 w-full" />
                    <Skeleton className="h-10 w-full" />
                  </div>
                ) : subscriptions.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    Este cliente não possui assinaturas.
                  </div>
                ) : (
                  <div className="w-full overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Plano</TableHead>
                          <TableHead>Quantidade</TableHead>
                          <TableHead>Valor Unit.</TableHead>
                          <TableHead>Cobrança</TableHead>
                          <TableHead>Valor Total</TableHead>
                          <TableHead>Data de Início</TableHead>
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {subscriptions.map((subscription) => (
                          <TableRow key={subscription.id} className="cursor-pointer hover:bg-muted/50" onClick={() => navigate(`/subscriptions/${subscription.id}`)}>
                            <TableCell className="font-extrabold">{subscription.subscription_plan?.name || '-'}</TableCell>
                            <TableCell>{subscription.quantity || 0}</TableCell>
                            <TableCell>
                              {formatCurrency(
                                subscription.unit_price || 0, 
                                (subscription.subscription_plan?.prices && 
                                Array.isArray(subscription.subscription_plan.prices) && 
                                subscription.subscription_plan.prices.length > 0) 
                                  ? subscription.subscription_plan.prices[0].currency 
                                  : 'BRL'
                              )}
                            </TableCell>
                            <TableCell>{getBillingFrequency(subscription.subscription_plan?.billing_period_type || 'monthly')}</TableCell>
                            <TableCell>
                              {formatCurrency(
                                (subscription.unit_price || 0) * (subscription.quantity || 0), 
                                (subscription.subscription_plan?.prices && 
                                Array.isArray(subscription.subscription_plan.prices) && 
                                subscription.subscription_plan.prices.length > 0) 
                                  ? subscription.subscription_plan.prices[0].currency 
                                  : 'BRL'
                              )}
                            </TableCell>
                            <TableCell>{formatDate(subscription.start_date)}</TableCell>
                            <TableCell>
                              <StatusBadge status={subscription.state} context="subscription" />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Conteúdo da Tab: Contatos */}
          <TabsContent value="contacts" className="mt-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Contatos</CardTitle>
                <Button 
                  variant="outline" 
                  onClick={() => setIsImportContactDialogOpen(true)}
                  className="gap-2"
                >
                  <Download className="h-4 w-4" />
                  Importar Contato
                </Button>
              </CardHeader>
              <CardContent>
                <CustomerContactsTable
                  customerId={customer?.id ?? ''}
                  refreshTrigger={contactsRefreshTrigger}
                  hideCreateButton={false}
                />
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
      
      {/* Import Contact Dialog */}
      <ImportHubspotContactDialog
        customerId={id as string}
        isOpen={isImportContactDialogOpen}
        onClose={() => setIsImportContactDialogOpen(false)}
        onContactImported={handleContactImported}
      />
    </div>
  );
};

export default CustomerDetail; 